import React, { useState, MouseEvent, useContext, useRef } from 'react';
import { Box, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import { i18n } from 'src/common/i18n/i18n';
import { Case } from 'src/common/models/common-types';
import { COLORS } from 'src/common/constants/constants';
import { GlobalContext } from 'src/common/contexts/globalContext';
import { CaseInfoDialog, WarningDialog, CaseDialog } from 'src/common/dialogs';
import { CommonCasesTree } from 'src/common/components';
import {
    IconSubcaseToParent,
    IconArrowDown,
    IconFolder,
    IconFolderOpen,
    IconArrowUp,
    IconThreeDotsOptions,
    IconDetails,
    IconMoveTo,
    IconDeleteAction,
    IconRename,
} from 'src/common/images';
import OptionsIconContainer from '../CasesTreeItem/OptionsIconContainer';
import { GenericTreeItem } from 'src/common/components/CommonTreeView/CommonTreeView';
import { usePopup } from '@cellebrite/saas-ui-common';
import { PENDO_EVENTS } from '../../../../../../../../common/utils/pendo';

interface CaseOptionsProps {
    caseNode: Case;
    OptionsIcon?: any;
    contextMenu: {
        mouseX: number;
        mouseY: number;
    } | null;
    optionsButton?: boolean;
    closeContextMenu?: () => void;
    onOpenMenu?: () => void;
    renameCaseFn?: () => void;
}

const CaseOptions: React.FC<CaseOptionsProps> = ({
    caseNode,
    OptionsIcon,
    contextMenu,
    closeContextMenu,
    onOpenMenu,
    renameCaseFn,
    optionsButton,
}) => {
    const { casesData, dialog } = useContext(GlobalContext);
    const [isBtnActive, setIsBtnActive] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>();
    const [btnClickPosition, setBtnClickPosition] = useState({ top: 0, left: 0 });
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [moveToOpen, moveToAnchorEl, MoveToHandleClick, MoveToHandleClose] = usePopup(null, () => setAnchorEl(null));
    let open = Boolean(anchorEl || contextMenu);

    const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
        setIsBtnActive(true);
        if (onOpenMenu) {
            onOpenMenu();
        }

        setBtnClickPosition(
            optionsButton && buttonRef && buttonRef.current
                ? { top: buttonRef.current.offsetTop + 47, left: e.clientX - 20 }
                : { top: e.clientY, left: e.clientX },
        );

        e.stopPropagation();
        setAnchorEl(e.currentTarget);
    };

    const handleClose = (e: MouseEvent) => {
        setIsBtnActive(false);
        e.stopPropagation();
        setAnchorEl(null);
        if (closeContextMenu) {
            closeContextMenu();
        }
    };

    const handleShowCaseInfoClose = (caseName: string, caseDescription: string, messageKeys?: any) =>
        casesData.updateCase({ caseId: caseNode.id, caseData: { name: caseName, description: caseDescription } });

    const onShowCaseInfo = (e: MouseEvent) => {
        dialog.open(CaseInfoDialog, { caseNode, onCloseFunc: handleShowCaseInfoClose });
        handleClose(e);
    };

    const onCreateSubCase = (e: MouseEvent) => {
        dialog.open(CaseDialog, {
            title: i18n.translate('Create new sub-case'),
            parentCase: caseNode.name,
            onSave: (name: string, description: string) => {
                casesData.createCase({
                    pendoEventName: PENDO_EVENTS.CREATE_SUB_CASE,
                    caseData: {
                        name,
                        description,
                        parentId: caseNode.id,
                    },
                });
            },
        });

        handleClose(e);
    };

    const onDeleteCase = (e: MouseEvent) => {
        dialog.open(WarningDialog, {
            title: i18n.translate('Delete Case'),
            message: i18n.translate('deleteCaseMessage', { caseName: caseNode.name }),
            actionBtnText: i18n.translate('DELETE CASE'),
            onSave: () => casesData.deleteCase(caseNode),
        });
        handleClose(e);
    };

    const moveToCaseHandler = (item: GenericTreeItem) => {
        if (item.id !== casesData.activeCaseId) {
            casesData.moveCase({
                caseNode: { ...caseNode, parentId: item.id as string },
                destination: item.id as string,
            });
            MoveToHandleClose();
        }
    };

    const onTurnSubcaseToRootParentCase = (e: MouseEvent) => {
        casesData.moveCase({
            caseNode: { ...caseNode, parentId: casesData.cases?.[0].parentId },
            destination: casesData.cases?.[0].parentId,
        });
        handleClose(e);
    };

    return (
        <>
            {optionsButton ? (
                <Box color="tetriary" sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconFolderOpen className="fill-b" style={{ width: '16px', height: '12px' }} />
                    <Typography
                        onClick={handleClick}
                        className="color-b"
                        sx={{
                            'ml': '8px',
                            'mr': '10px',
                            'fontWeight': 'bold',
                            'fontSize': '16px',
                            'cursor': 'pointer',
                            '&:hover': {
                                borderBottom: `1px solid ${COLORS.BRAND}`,
                            },
                        }}
                    >
                        {caseNode.name}
                    </Typography>
                    <IconButton
                        onClick={handleClick}
                        ref={buttonRef}
                        sx={{ '&:hover': { backgroundColor: COLORS.BRAND_LIGHTEST } }}
                    >
                        {isBtnActive ? <IconArrowUp className="fill-b" /> : <IconArrowDown className="fill-b" />}
                    </IconButton>
                </Box>
            ) : (
                <IconButton className="fill-md" sx={{ mr: 1, p: 1 }} type="button" onClick={handleClick}>
                    {OptionsIcon ? <OptionsIcon style={{ marginTop: '4px' }} /> : <IconThreeDotsOptions />}
                </IconButton>
            )}

            <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={open || contextMenu !== null}
                onClose={handleClose}
                MenuListProps={{
                    'aria-labelledby': 'basic-button',
                }}
                anchorReference="anchorPosition"
                anchorPosition={
                    contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : btnClickPosition
                }
            >
                <MenuItem onClick={onShowCaseInfo}>
                    <OptionsIconContainer>
                        <IconDetails className="fill-md" />
                    </OptionsIconContainer>
                    {i18n.translate('Details')}
                </MenuItem>
                {renameCaseFn && (
                    <MenuItem
                        onClick={(e) => {
                            handleClose(e);
                            renameCaseFn();
                        }}
                    >
                        <OptionsIconContainer>
                            <IconRename className="fill-md" />
                        </OptionsIconContainer>
                        {i18n.translate('Rename')}
                    </MenuItem>
                )}
                {caseNode.parentId !== casesData.cases?.[0].parentId && (
                    <MenuItem onClick={onTurnSubcaseToRootParentCase}>
                        <OptionsIconContainer>
                            <IconSubcaseToParent className="fill-md" />
                        </OptionsIconContainer>
                        {i18n.translate('Set as a parent case')}
                    </MenuItem>
                )}

                <MenuItem onClick={MoveToHandleClick}>
                    <OptionsIconContainer>
                        <IconMoveTo className="fill-md" />
                    </OptionsIconContainer>
                    {i18n.translate('Move to')}
                </MenuItem>
                <MenuItem onClick={onCreateSubCase}>
                    <OptionsIconContainer>
                        <IconFolder className="fill-md" />
                    </OptionsIconContainer>
                    {i18n.translate('New sub-case')}
                </MenuItem>
                <MenuItem onClick={onDeleteCase}>
                    <OptionsIconContainer>
                        <IconDeleteAction className="fill-md" />
                    </OptionsIconContainer>
                    {i18n.translate('Delete')}
                </MenuItem>
            </Menu>

            <Menu
                anchorEl={moveToAnchorEl}
                open={moveToOpen}
                onClose={MoveToHandleClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                <CommonCasesTree action={moveToCaseHandler} />
            </Menu>
        </>
    );
};

export default CaseOptions;
